
import { defineComponent, PropType } from 'vue';
import type { FlightLookupItem } from '@white-label-types/parking-checkout';
import TextField from '../text-field/text-field.vue';

import { IconMessage } from 'ui-shared-components';

export default defineComponent({
  name: 'FlightLookup',
  components: {
    TextField,
    IconButton: () => import('@white-label-icon/icon-button'),
    IconAlert: () => import('@white-label-icon/icon-alert'),
    IconCheck: () => import('@white-label-icon/icon-check'),
    IconAskMark: () => import('@white-label-icon/icon-ask-mark'),
    IconMessage,
  },

  props: {
    items: {
      type: Array as PropType<FlightLookupItem[]>,
      required: true,
    },
    value: {
      type: Object | null,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    direction: {
      type: String,
      default: 'inbound',
    },
    error: {
      type: Object | null,
      default: () => { },
    },
    errorLabel: {
      type: String,
    },
    inputType: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
    },
    flightChangeEnabled: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      searchQuery: '',
      filteredItems: [],
      dropdownOpened: false,
      isTooltipVisible: false,
      isEditing: false,
      activeItem: null as FlightLookupItem | null, // New state for active option
      activeDescendantId: null as string | null, // New state for active id element
    };
  },
  computed: {
    prependIcon() {
      return () => import('@white-label-icon/icon-search');
    },

    localValue: {
      get(): FlightLookupItem | null {
        return this.value;
      },

      set(val: FlightLookupItem | null) {
        this.$emit('input', val);
      },
    },
    isInbound(): boolean {
      return this.direction === 'inbound';
    },
  },
  watch: {
    searchQuery() {
      this.filterItems();
    },
    items: {
      deep: true,
      handler() {
        this.filterItems();
      }
    },
  },
  created() {
    this.filterItems();
  },
  methods: {
    filterItems() {
      const query = this.searchQuery.toLowerCase();
      this.filteredItems = this.items.filter(item => {
        const airlineName = item.airlineName?.toLowerCase().includes(query);
        const terminal = item.terminalName ? item.terminalName.toLowerCase().includes(query) : false;
        const flightNumber = item.flightNumber?.toLowerCase().includes(query);
        const city = item.city?.toLowerCase().includes(query);
        const airport = item.airport?.toLowerCase().includes(query);

        return airlineName || terminal || flightNumber || city || airport;
      });
    },
    openDropdown() {
      if (this.loading) return;

      this.dropdownOpened = true;
      if (this.filteredItems.length > 0) {
        this.activeItem = this.filteredItems[0];
        this.updateActiveDescendant();
      }
    },
    closeDropdown(): void {
      this.dropdownOpened = false;
      this.activeItem = null;
      this.activeDescendantId = null;
    },
    selectItem(item: FlightLookupItem) {
      this.isEditing = false;
      this.localValue = item;
      this.closeDropdown();
    },
    clearSelection() {
      this.searchQuery = '';
      this.localValue = null;
    },
    editSelection() {
      this.isEditing = true;
      this.localValue = null;
      this.openDropdown();

      this.$nextTick(() => {
        if (this.$refs.comboboxWrapper) {
          this.$refs.comboboxWrapper.focus();
        }
      });
    },
    // New methods for keyboard navigation
    navigateDown() {
      if (!this.dropdownOpened) {
        this.openDropdown();
        return;
      }
      if (!this.filteredItems.length) return;

      const currentIndex = this.filteredItems.findIndex(item => item === this.activeItem);
      const nextIndex = (currentIndex + 1) % this.filteredItems.length;
      this.activeItem = this.filteredItems[nextIndex];
      this.updateActiveDescendant();
      this.scrollIntoView();
    },
    navigateUp() {
      if (!this.dropdownOpened) return;
      if (!this.filteredItems.length) return;

      const currentIndex = this.filteredItems.findIndex(item => item === this.activeItem);
      const prevIndex = (currentIndex - 1 + this.filteredItems.length) % this.filteredItems.length;
      this.activeItem = this.filteredItems[prevIndex];
      this.updateActiveDescendant();
      this.scrollIntoView();
    },
    updateActiveDescendant() {
      if (this.activeItem) {
        this.activeDescendantId = 'option-' + this.activeItem.id;
      } else {
        this.activeDescendantId = null;
      }
    },
    scrollIntoView() {
      this.$nextTick(() => {
        const activeEl = document.getElementById(this.activeDescendantId);
        if (activeEl) {
          activeEl.scrollIntoView({ block: 'nearest' });
        }
      });
    },
    setActiveItem(item: any) {
      this.activeItem = item;
      this.updateActiveDescendant();
    },
    handleEnterKey() {
      if (this.activeItem) {
        this.selectItem(this.activeItem);
      } else {
        this.openDropdown();
      }
    }
  },
});
